<template>
    <div>
        <MonitorLayoutTemplate
            :table_title="table_title"
        >
            <template #datetime>
                <QpeDatetime
                    :props_date="date"
                    :props_time="time"
                    :name="default_value"
                    @update-time="update_time"
                    @change-parent-date-time="change_parent_date_time"
                />
            </template>

            <template #description>
                <SignsCalculator 
                    :data="sign_data"
                />
                <MapDisplaySwitch 
                    :table_id="default_value"
                    :table_data="immutable_table_data"
                />
            </template>

            <template #signs>
                <MonitorSigns
                    :data="sign_data"
                    :enable_display_tooltip="true"
                />
            </template>

            <template #data_table="slot_props">
                <QpeDatatable
                    :data="table_data"
                    :headers="header"
                    :name="default_value"
                    :error_text="error_text"
                    :search="slot_props.search"
                    :should_draw_diagram_header="should_draw_diagram_header"
                    :enable_diagram_slot="true"
                    :should_display_info_header_key_value="should_display_info_header_key_value"
                    :generate_tooltop_display_info="generate_tooltop_display_info_for_table"
                    :should_show_raw_html_header="should_show_raw_html_header"
                    :show_download_doc_btn="true"
                    :show_download_pdf_btn="show_download_pdf_btn"
                    @update-time="update_time"
                    @change-parent-date-time="change_parent_date_time"
                >
                    <template #table_description>
                        <div class="long-explanations">
                            說明:<br>
                            <ol class="long-explanations__decimal-layer">
                                <li>「--」 表示該欄位無資料。</li>
                                <li>即時資料為整點觀測，約50分鐘後可得，例如00時之觀測約於00時50分顯示。</li>
                                <li>
                                    浮標說明：<br>
                                    <ul class="long-explanations__hide-layer">
                                        <li>
                                            <mark>*花蓮資料浮標：</mark>花蓮七星潭海域，水深22m處經緯度：24°01'57''N，121°37'51''E
                                        </li>
                                        <li class="long-explanations__indent">
                                            所屬單位：中央氣象署
                                        </li>
                                        <li>
                                            <mark>*臺東資料浮標：</mark>距臺東海堤約3km，臺東焚化爐外1km，水深30m處經緯度：24°43'24''N，121°08'37''E
                                        </li>
                                        <li class="long-explanations__indent">
                                            所屬單位：經濟部水利署
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    台17線灣裡路段為臺南市政府代管路段，其浪襲監控仍待實際應變作業操作後，予以修正相關設定。<br>
                                    <ul class="long-explanations__hide-layer">
                                        <li>
                                            <mark>*小琉球資料浮標，位置(水深)：</mark>枋寮恆春沿海，小琉球海子口西南方外海約4km，該處水深約82m處。經緯度：23.3139°N，120.3514°E
                                        </li>
                                        <li class="long-explanations__indent">
                                            所屬單位：中央氣象署
                                        </li>
                                        <li>
                                            <mark>*七美資料浮標，位置(水深)：</mark>澎湖海面，澎湖縣七美鄉南方30公里,布放水深90公尺。經緯度：119.5175°E，22.9502°N
                                        </li>
                                        <li class="long-explanations__indent">
                                            所屬單位：中央氣象署
                                        </li>
                                    </ul>
                                </li>
                            </ol>
                        </div>
                    </template>
                </QpeDatatable>
            </template>
        </MonitorLayoutTemplate>
    </div>
</template>

<script>
    import { redirect_monitor_table_ajax } from "@/utils/monitor/monitor.js"
    import { judge_invalid_data_by_keys, sort_table_data } from "@/utils/monitor/common_tool"
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import cloneDeep from 'lodash/cloneDeep'
    import {
        TABLE_VALUE_TEXT_MAPPING,
        monitor_signs_normal_mapping,
        monitor_signs_icon_name_mapping, 
        monitor_signs_normal_mapping_css
    } from "@/config/monitor_config.js"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import SignsCalculator from "@/components/monitor/_tool/signs_calculator.vue";
    import MapDisplaySwitch from "@/components/monitor/_tool/map_display_switch.vue"
    import MonitorSigns from "@/components/monitor/_tool/monitor_signs.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"

    export default {
        name: 'WaveMonitor',
        
        components: { 
            MonitorLayoutTemplate,
            QpeDatetime,
            SignsCalculator,
            MapDisplaySwitch,
            QpeDatatable, 
            MonitorSigns, 
        },

        mixins: [monitor_mixin],

        props: {
            default_value: {
                type: Number,
                required: true,
            },

            in_overview_page: {
                type: Boolean,
                default() {
                    return false;
                }
            },
        },

        data() {
            return {
                // 排序使用
                table_data: [],
                header: [],
                error_text: '',

                immutable_table_data: [],

                should_draw_diagram_header: ['10分鐘', '1小時', '3小時', '6小時', '12小時', '24小時'],

                history_data: [],
                current_table_header: '',
                current_choosed_table_value: '',
                history_data_loaded: false,

                should_display_info_header_key_value: {
                    '警示理由': {},
                },

                should_show_raw_html_header: ['警示'],
            }
        },
        
        computed: {
            table_title() {
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            },
        
            sign_data() {
                return this.immutable_table_data.map((item) => {
                    const each_sign_data = {
                        idx: item['項次'] || item['編號'],
                        signs_num: monitor_signs_normal_mapping[item['警示']] || '-'
                    }
                    return each_sign_data
                });
            },

            enable_filter_unalert_data() {
                return this.in_overview_page
            },

            show_download_pdf_btn() {
                return !this.in_overview_page
            },
        },

        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },

        methods: {
            generate_table_data(data, header) {
                this.immutable_table_data = data;
                this.header = header;
                let table_data = data
                if(this.enable_filter_unalert_data) {
                    table_data = data.filter(datum => judge_invalid_data_by_keys(datum, ['警示']));
                }
                this.table_data = this.reconstruct_alert_content(sort_table_data(table_data, monitor_signs_icon_name_mapping));
            },

            reconstruct_alert_content(data_array) {
                return cloneDeep(data_array).map(datum => {
                    const be_concat_object = {}
                    this.should_show_raw_html_header.forEach(html_header => {
                        if(monitor_signs_icon_name_mapping[datum[html_header]]) {
                            const signal_class = monitor_signs_normal_mapping_css[datum[html_header]]
                            const icon_class = monitor_signs_icon_name_mapping[datum[html_header]]

                            be_concat_object[html_header] = [{
                                'html': `<i class="v-icon ${signal_class} ${icon_class}"></i>`,
                                'td_class': `td__${icon_class}`
                            }]
                        }
                    })
                    return Object.assign(datum, be_concat_object)
                }) 
            },

            generate_tooltop_display_info_for_table(row) {
                return row['警示條件']
            },
        },
    }
</script>

<style lang="scss" scoped>
    .long-explanations {
        padding: .5rem 1rem;
        font-size: 15px;

        &__decimal-layer {
            list-style-type: decimal;
        }

        &__hide-layer {
            list-style-type: none;
        }

        &__indent {
            text-indent: 2rem;
        }
    }

    mark {
        color: #16a9ff;
        background-color: transparent;
    }
</style>